import type { Component } from 'vue';

import KUICoreLoaderAnyway from '~kui/components/KUI/Core/Loader/KUICoreLoaderAnyway.vue';
import { defineAsyncComponent } from 'vue';

import type { Article } from '~/types/Learn/Article';

import { ArticleTemplate } from '~/graphql/generated/graphql';

// The editor host has a sync setup, so an unresolved async component would render
// nothing there — paint the loader immediately instead of a blank preview pane.
const asyncComponent = (loader: () => Promise<Component>): Component =>
  defineAsyncComponent({ loader, loadingComponent: KUICoreLoaderAnyway, delay: 0 });

export interface ArticleTemplateKeepReading {
  id: string;
  name: string;
  /** Resolved public path (see articlePublicPath). */
  to: string;
}

/**
 * The one call site that renders an article picks a template component by key, so every
 * template is invoked with these props — declared once so a prop added for one template
 * can't silently miss the others.
 */
export interface ArticleTemplateProps {
  article: Article;
  preview?: boolean;
  keepReading?: ArticleTemplateKeepReading[];
  signUpLink?: string;
  showSignUpCta?: boolean;
}

export interface ArticleTemplateDefinition {
  key: ArticleTemplate;
  labelKey: string;
  /** Header badge i18n key; null renders the generic article badge. */
  badgeKey: string | null;
  component: Component;
  /** Template-specific editor field block; null is an explicit "no extra fields". */
  editorFields: Component | null;
}

export const DEFAULT_ARTICLE_TEMPLATE = ArticleTemplate.DefaultBlog;

/**
 * The single definition of a template: reader component, picker label, header
 * badge and editor fields in one exhaustive Record — a new enum case fails
 * typecheck here until every column is decided. Both component columns load
 * lazily, so the public bundle never pulls editor code and a reader only
 * downloads the template their article renders; the picker reads labelKey
 * alone, so the registry stays cheap to import.
 */
export const ARTICLE_TEMPLATES: Record<ArticleTemplate, ArticleTemplateDefinition> = {
  [ArticleTemplate.DefaultBlog]: {
    key: ArticleTemplate.DefaultBlog,
    labelKey: 'content.articles.templates.defaultBlog',
    badgeKey: null,
    component: asyncComponent(() => import('~/components/Article/ArticleTemplateDefaultBlog.vue')),
    editorFields: null,
  },
  [ArticleTemplate.StepByStepGuide]: {
    key: ArticleTemplate.StepByStepGuide,
    labelKey: 'content.articles.templates.stepByStepGuide',
    badgeKey: 'unified.articles.badges.stepByStepGuide',
    component: asyncComponent(() => import('~/components/Article/ArticleTemplateStepByStepGuide.vue')),
    editorFields: asyncComponent(() => import('~/pages/content/articles/components/ArticleStepByStepGuideFields.vue')),
  },
  [ArticleTemplate.CareerPathwayMap]: {
    key: ArticleTemplate.CareerPathwayMap,
    labelKey: 'content.articles.templates.careerPathwayMap',
    badgeKey: 'unified.articles.badges.careerPathwayMap',
    component: asyncComponent(() => import('~/components/Article/ArticleTemplateCareerPathwayMap.vue')),
    editorFields: asyncComponent(() => import('~/pages/content/articles/components/ArticleCareerPathwayMapFields.vue')),
  },
  [ArticleTemplate.RankedCards]: {
    key: ArticleTemplate.RankedCards,
    labelKey: 'content.articles.templates.rankedCards',
    badgeKey: 'unified.articles.badges.rankedCards',
    component: asyncComponent(() => import('~/components/Article/ArticleTemplateRankedCards.vue')),
    editorFields: asyncComponent(() => import('~/pages/content/articles/components/ArticleRankedCardsFields.vue')),
  },
  [ArticleTemplate.SalaryGuide]: {
    key: ArticleTemplate.SalaryGuide,
    labelKey: 'content.articles.templates.salaryGuide',
    badgeKey: 'unified.articles.badges.salaryGuide',
    component: asyncComponent(() => import('~/components/Article/ArticleTemplateSalaryGuide.vue')),
    editorFields: asyncComponent(() => import('~/pages/content/articles/components/ArticleSalaryGuideFields.vue')),
  },
  [ArticleTemplate.ComparisonGuide]: {
    key: ArticleTemplate.ComparisonGuide,
    labelKey: 'content.articles.templates.comparisonGuide',
    badgeKey: 'unified.articles.badges.comparisonGuide',
    component: asyncComponent(() => import('~/components/Article/ArticleTemplateComparisonGuide.vue')),
    editorFields: asyncComponent(() => import('~/pages/content/articles/components/ArticleComparisonGuideFields.vue')),
  },
  [ArticleTemplate.PractitionerStories]: {
    key: ArticleTemplate.PractitionerStories,
    labelKey: 'content.articles.templates.practitionerStories',
    badgeKey: 'unified.articles.badges.practitionerStories',
    component: asyncComponent(() => import('~/components/Article/ArticleTemplatePractitionerStories.vue')),
    editorFields: asyncComponent(() => import('~/pages/content/articles/components/ArticlePractitionerStoriesFields.vue')),
  },
  [ArticleTemplate.DayInTheLife]: {
    key: ArticleTemplate.DayInTheLife,
    labelKey: 'content.articles.templates.dayInTheLife',
    badgeKey: 'unified.articles.badges.dayInTheLife',
    component: asyncComponent(() => import('~/components/Article/ArticleTemplateDayInTheLife.vue')),
    editorFields: asyncComponent(() => import('~/pages/content/articles/components/ArticleDayInTheLifeFields.vue')),
  },
  [ArticleTemplate.QuizLanding]: {
    key: ArticleTemplate.QuizLanding,
    labelKey: 'content.articles.templates.quizLanding',
    badgeKey: 'unified.articles.badges.quizLanding',
    component: asyncComponent(() => import('~/components/Article/ArticleTemplateQuizLanding.vue')),
    editorFields: asyncComponent(() => import('~/pages/content/articles/components/ArticleQuizLandingFields.vue')),
  },
  [ArticleTemplate.Explainer]: {
    key: ArticleTemplate.Explainer,
    labelKey: 'content.articles.templates.explainer',
    badgeKey: 'unified.articles.badges.explainer',
    component: asyncComponent(() => import('~/components/Article/ArticleTemplateExplainer.vue')),
    editorFields: asyncComponent(() => import('~/pages/content/articles/components/ArticleExplainerFields.vue')),
  },
  [ArticleTemplate.EditorialEssay]: {
    key: ArticleTemplate.EditorialEssay,
    labelKey: 'content.articles.templates.editorialEssay',
    badgeKey: 'unified.articles.badges.editorialEssay',
    component: asyncComponent(() => import('~/components/Article/ArticleTemplateEditorialEssay.vue')),
    editorFields: null,
  },
};

export const resolveArticleTemplate = (key: string | null | undefined): ArticleTemplateDefinition =>
  ARTICLE_TEMPLATES[(key as ArticleTemplate) ?? DEFAULT_ARTICLE_TEMPLATE] ?? ARTICLE_TEMPLATES[DEFAULT_ARTICLE_TEMPLATE];
